import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
import { FileText, Download, TrendingUp, DollarSign, Receipt, Users, Eye } from "lucide-react";
import { auth, db } from "@/firebase/config";
import { collection, doc, getDoc, getDocs, limit, onSnapshot, query, where } from "firebase/firestore";
import { getIdTokenResult, onAuthStateChanged } from "firebase/auth";

const reportStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
  
  :root {
    --primary: #1e293b;
    --secondary: #475569;
    --accent: #2563eb;
    --border: #e2e8f0;
    --bg-subtle: #f8fafc;
  }

  .report-container {
    font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif;
    padding: 40px;
    color: #0f172a;
    line-height: 1.5;
    font-size: 12px;
    max-width: 1000px;
    margin: 0 auto;
    background: white;
  }

  .report-container * { box-sizing: border-box; }

  /* Header Styles */
  .report-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 40px;
    padding-bottom: 20px;
    border-bottom: 2px solid var(--primary);
  }
  
  .school-info h1 {
    font-size: 24px;
    font-weight: 700;
    color: var(--primary);
    margin: 0 0 4px 0;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }
  
  .school-info p {
    color: var(--secondary);
    margin: 0;
    font-size: 11px;
  }

  .report-meta {
    text-align: right;
  }

  .report-meta h2 {
    font-size: 14px;
    font-weight: 600;
    color: var(--secondary);
    margin: 0 0 4px 0;
    text-transform: uppercase;
  }

  .meta-item {
    font-size: 11px;
    color: #64748b;
    margin-bottom: 2px;
  }

  /* Section Styles */
  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--primary);
    margin: 30px 0 15px 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .section-title::before {
    content: '';
    display: block;
    width: 4px;
    height: 16px;
    background: var(--accent);
    border-radius: 2px;
  }

  /* Summary Cards */
  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
    margin-bottom: 30px;
  }

  .summary-card {
    background: var(--bg-subtle);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 15px;
  }

  .summary-label {
    font-size: 11px;
    color: var(--secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }

  .summary-value {
    font-size: 18px;
    font-weight: 700;
    color: var(--primary);
  }

  .text-green { color: #059669; }
  .text-red { color: #dc2626; }

  /* Table Styles */
  .report-container table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 30px;
    font-size: 11px;
  }

  .report-container th {
    background: var(--bg-subtle);
    color: var(--secondary);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.5px;
    padding: 12px 10px;
    text-align: left;
    border-bottom: 1px solid var(--border);
  }

  .report-container td {
    padding: 10px;
    border-bottom: 1px solid var(--border);
    color: #334155;
  }

  .report-container tr:last-child td {
    border-bottom: none;
  }

  .text-right { text-align: right; }
  .font-medium { font-weight: 500; }
  .font-bold { font-weight: 700; }

  /* Footer */
  .report-footer {
    margin-top: 50px;
    padding-top: 20px;
    border-top: 1px solid var(--border);
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: #94a3b8;
  }

  @media print {
    .report-container { padding: 0; }
    .no-break { break-inside: avoid; }
  }
`;

// Helper: determine if a transaction status represents a successful, settled inflow/outflow
function isSuccessfulStatus(status: any) {
  const v = String(status ?? '').toLowerCase();
  return v === 'completed' || v === 'success' || v === 'successful' || v === 'paid' || v === 'settled';
}

  type StudentFeeRow = {
    id: string;
    studentName: string;
    registrationNumber: string;
    className: string;
    totalFees: number;
    paidAmount: number;
    balance: number;
    status: 'paid' | 'partial' | 'unpaid';
    studentType?: string;
  };

  type ClassSummaryRow = {
    className: string;
    students: number;
    collected: number;
    outstanding: number;
    amountOutstanding: number;
    amountCollected: number;
  };

  const FinanceReports = () => {
    const [selectedPeriod, setSelectedPeriod] = useState("month");
    const [schoolID, setSchoolID] = useState<string | null>(null);
    const [schoolName, setSchoolName] = useState<string>("School");
    const [schoolLogo, setSchoolLogo] = useState<string | null>(null);
    const [schoolAddress, setSchoolAddress] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [classSummary, setClassSummary] = useState<ClassSummaryRow[]>([]);
    const [incomeVsExpenses, setIncomeVsExpenses] = useState<Array<{ month: string; income: number; expenses: number }>>([]);
    const [cashFlowData, setCashFlowData] = useState<Array<{ month: string; cashFlow: number }>>([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewType, setPreviewType] = useState<'fee-collection' | 'income-statement' | null>(null);
    const printRef = useRef<HTMLDivElement>(null);

    // Date range helper for period filtering (assumption: Kenyan school terms)
    const periodRange = useMemo(() => {
      const now = new Date();
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      if (selectedPeriod === 'week') {
        const day = now.getDay();
        const diff = (day === 0 ? 6 : day - 1); // Monday as start
        start.setDate(now.getDate() - diff);
      } else if (selectedPeriod === 'month') {
        start.setDate(1);
      } else if (selectedPeriod === 'term') {
        // Assumed terms: T1 (Jan-Mar), T2 (May-Jul), T3 (Sep-Nov)
        const m = now.getMonth();
        if (m <= 2) { start.setMonth(0, 1); end.setMonth(2, 31); }
        else if (m <= 6) { start.setMonth(4, 1); end.setMonth(6, 31); }
        else if (m <= 10) { start.setMonth(8, 1); end.setMonth(10, 30); }
        else { // Dec falls back to T3 window ending Nov 30, include Dec as grace
          start.setMonth(8, 1); end.setMonth(11, 31);
        }
      } else if (selectedPeriod === 'year') {
        start.setMonth(0, 1);
      }
      return { start, end };
    }, [selectedPeriod]);

    const periodLabel = useMemo(() => {
      switch (selectedPeriod) {
        case 'week': return 'this week';
        case 'month': return 'this month';
        case 'term': return 'this term';
        case 'year': return 'this year';
        default: return '';
      }
    }, [selectedPeriod]);

    useEffect(() => {
      const unsub = onAuthStateChanged(auth, async (u) => {
        if (!u) { setSchoolID(null); return; }
        try {
          const token = await getIdTokenResult(u, true);
          const affiliations = (token.claims.affiliations as Array<{ role: string; schoolID?: string }> | undefined) || [];
          const defaultAff = token.claims.defaultAffiliation as { role?: string; schoolID?: string } | undefined;
          const pick =
            (affiliations.find(a => a.role === 'admin' && a.schoolID?.trim()) ||
             affiliations.find(a => a.role === 'school_staff' && a.schoolID?.trim()) ||
             defaultAff);
          const sid = pick?.schoolID?.trim() || null;
          setSchoolID(sid);
        } catch { setSchoolID(null); }
      });
      return () => unsub();
    }, []);

    // Refs to hold snapshot docs per provider & active unsubscribers
    const providerSnapshots = useRef<Record<string, any[]>>({});
    const liveUnsubs = useRef<Array<() => void>>([]);

    useEffect(() => {
      if (!schoolID) return;
      (async () => {
        setLoading(true);
        try {
          // School name
          try {
            const schDoc = await getDoc(doc(db, `schools/${schoolID}/schoolDetails/main`));
            if (schDoc.exists()) {
              const data = schDoc.data();
              setSchoolName(String(data.name || 'School'));
              setSchoolLogo(data.logo || null);
              setSchoolAddress(data.address || data.location || '');
            }
          } catch {}

          // Fee structure
          const fsDoc = await getDoc(doc(db, `schools/${schoolID}/deployedFeeStructures/current`));
          const levelFees = (fsDoc.exists() ? (fsDoc.data().levelFees || []) : []) as Array<{
            level: string;
            classes: string[];
            categories: Array<{ name: string; term1: number; term2: number; term3: number; daySchoolTerm1?: number; daySchoolTerm2?: number; daySchoolTerm3?: number; paidOnce?: boolean; }>;
            isGovernmentSponsored?: boolean;
            governmentSponsorshipAmount?: number;
          }>;

          // Students
          const studentsSnap = await getDocs(query(collection(db, `schools/${schoolID}/students`)));

          const computeTotalFees = (student: any) => {
            const studentClass = student.className || student.class || '';
            const studentGrade = student.admissionGrade || student.grade || '';
            const studentType = student.studentType || '';
            let matching: any = null;
            if (studentClass) matching = levelFees.find((lvl: any) => lvl.classes?.some((c: string) => c?.toLowerCase?.() === studentClass?.toLowerCase?.() || studentClass?.toLowerCase?.().includes(c?.toLowerCase?.())));
            if (!matching && studentGrade) matching = levelFees.find((lvl: any) => String(lvl.level || '').toLowerCase() === String(studentGrade || '').toLowerCase());
            if (!matching) return 0;
            const isDay = studentType === 'Day School';
            const cats = matching.categories || [];
            let grossT1 = 0, grossT2 = 0, grossT3 = 0;
            cats.forEach((cat: any) => {
              const v1 = isDay ? (cat.daySchoolTerm1 ?? cat.term1 ?? 0) : (cat.term1 ?? 0);
              const v2 = isDay ? (cat.daySchoolTerm2 ?? cat.term2 ?? 0) : (cat.term2 ?? 0);
              const v3 = isDay ? (cat.daySchoolTerm3 ?? cat.term3 ?? 0) : (cat.term3 ?? 0);
              if (cat.paidOnce) { grossT1 += Number(v1) || 0; }
              else { grossT1 += Number(v1) || 0; grossT2 += Number(v2) || 0; grossT3 += Number(v3) || 0; }
            });
            let netT1 = grossT1, netT2 = grossT2, netT3 = grossT3;
            const gov = Number((matching.governmentSponsorshipAmount || 0) as number) || 0;
            if (matching.isGovernmentSponsored && gov > 0) {
              let rem = gov;
              const d1 = Math.min(grossT1, rem); netT1 -= d1; rem -= d1;
              const d2 = Math.min(grossT2, rem); netT2 -= d2; rem -= d2;
              const d3 = Math.min(grossT3, rem); netT3 -= d3;
            }
            return netT1 + netT2 + netT3;
          };

          // Build class summary via transactions
          const providers = ['mpesa', 'equity', 'kcb', 'cash', 'card', 'bank'];
          const rows: StudentFeeRow[] = [];
          for (const sDoc of studentsSnap.docs) {
            const s = sDoc.data();
            const reg = s.registrationNumber || '';
            const totalFees = computeTotalFees(s);
            let paidAmount = 0;
            for (const p of providers) {
              try {
                const qRef = query(
                  collection(db, `schools/${schoolID}/transactions/${p}/records`),
                  where('accountReference', '==', `Fee-${reg}`),
                  where('status', '==', 'completed'),
                  limit(500)
                );
                const snap = await getDocs(qRef);
                snap.forEach(d => {
                  const data: any = d.data();
                  const amt = Number(data?.amount || 0) || 0;
                  const type = String(data?.type || 'credit').toLowerCase();
                  paidAmount += type === 'debit' ? -amt : amt;
                });
              } catch {}
            }
            const balance = Math.max(0, totalFees - paidAmount);
            let status: 'paid' | 'partial' | 'unpaid' = 'unpaid';
            if (balance === 0 && totalFees > 0) status = 'paid';
            else if (paidAmount > 0 && balance > 0) status = 'partial';
            rows.push({ id: sDoc.id, studentName: `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Unknown', registrationNumber: reg, className: s.className || s.class || 'Unassigned', totalFees, paidAmount, balance, status, studentType: s.studentType || 'Boarding' });
          }

          const byClass = new Map<string, ClassSummaryRow>();
          rows.forEach(r => {
            const key = r.className || 'Unassigned';
            const curr = byClass.get(key) || { className: key, students: 0, collected: 0, outstanding: 0, amountOutstanding: 0, amountCollected: 0 };
            curr.students += 1;
            if (r.balance > 0) curr.outstanding += 1; else curr.collected += 1;
            curr.amountOutstanding += r.balance;
            curr.amountCollected += r.paidAmount;
            byClass.set(key, curr);
          });
          setClassSummary(Array.from(byClass.values()).sort((a, b) => a.className.localeCompare(b.className)));

          // Real-time income vs expenses (credits vs debits) by month, filtered by selected period.
          // Data sources: Firestore transactions per provider (mpesa, equity, kcb, cash, card)
          // Formula: CashFlowMonth = Sum(credit amounts with successful status) - Sum(debit amounts with successful status)
          // Only statuses considered successful are: completed/success/successful/paid/settled
          const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const monthAggRef = new Map<string, { income: number; expenses: number }>();
          const recompute = () => {
            const sortedMonths = Array.from(monthAggRef.entries()).sort((a,b) => {
              const [ma, ya] = a[0].split(' ');
              const [mb, yb] = b[0].split(' ');
              const mi = (n: string) => monthNames.indexOf(n);
              const da = new Date(Number(ya), mi(ma));
              const dbd = new Date(Number(yb), mi(mb));
              return da.getTime() - dbd.getTime();
            });
            const ive = sortedMonths.map(([m,v]) => ({ month: m, income: v.income, expenses: v.expenses }));
            setIncomeVsExpenses(ive);
            setCashFlowData(ive.map(x => ({ month: x.month, cashFlow: x.income - x.expenses })));
          };
          // Clean previous listeners
          liveUnsubs.current.forEach(fn => fn());
          liveUnsubs.current = [];
          providerSnapshots.current = {};
          const newUnsubs: Array<() => void> = [];
          providers.forEach(p => {
            try {
              const qBase = query(
                collection(db, `schools/${schoolID}/transactions/${p}/records`),
                where('createdAt', '>=', periodRange.start),
                where('createdAt', '<=', periodRange.end),
                where('status', '==', 'completed'),
                limit(1000)
              );
              const unsub = onSnapshot(qBase, snap => {
                // Remove previous entries from this provider by recomputing entire map across all providers snapshots (simplified approach)
                // Gather all docs from current provider snapshot combined with others stored temporarily.
                // Simplicity: Rebuild from scratch using aggregated snapshots we keep in a ref list.
                providerSnapshots.current[p] = snap.docs.map(d => d.data());
                monthAggRef.clear();
                Object.values(providerSnapshots.current).forEach((docs: any[]) => {
                  docs.forEach(data => {
                    const ts = data?.createdAt?._seconds ? new Date(data.createdAt._seconds * 1000) : (data?.createdAt?.toDate ? data.createdAt.toDate() : new Date());
                    if (ts < periodRange.start || ts > periodRange.end) return; // Guard if listener returns extra results
                    if (!isSuccessfulStatus((data as any)?.status)) return; // Exclude failed/pending/voided
                    const m = `${monthNames[ts.getMonth()]} ${ts.getFullYear()}`;
                    const rec = monthAggRef.get(m) || { income: 0, expenses: 0 };
                    const amt = Number(data?.amount || 0) || 0;
                    const type = String(data?.type || 'credit').toLowerCase();
                    if (type === 'debit') rec.expenses += amt; else rec.income += amt;
                    monthAggRef.set(m, rec);
                  });
                });
                recompute();
              }, () => {
                // Fallback: some envs may lack composite index for (createdAt, status). Retry without status where,
                // but keep in-memory status filter to avoid counting failed transactions.
                try {
                  const qAlt = query(
                    collection(db, `schools/${schoolID}/transactions/${p}/records`),
                    where('createdAt', '>=', periodRange.start),
                    where('createdAt', '<=', periodRange.end),
                    limit(1000)
                  );
                  const unsubAlt = onSnapshot(qAlt, snap2 => {
                    providerSnapshots.current[p] = snap2.docs.map(d => d.data());
                    monthAggRef.clear();
                    Object.values(providerSnapshots.current).forEach((docs: any[]) => {
                      docs.forEach(data => {
                        const ts = data?.createdAt?._seconds ? new Date(data.createdAt._seconds * 1000) : (data?.createdAt?.toDate ? data.createdAt.toDate() : new Date());
                        if (ts < periodRange.start || ts > periodRange.end) return;
                        if (!isSuccessfulStatus((data as any)?.status)) return; // in-memory status filter
                        const m = `${monthNames[ts.getMonth()]} ${ts.getFullYear()}`;
                        const rec = monthAggRef.get(m) || { income: 0, expenses: 0 };
                        const amt = Number(data?.amount || 0) || 0;
                        const type = String(data?.type || 'credit').toLowerCase();
                        if (type === 'debit') rec.expenses += amt; else rec.income += amt;
                        monthAggRef.set(m, rec);
                      });
                    });
                    recompute();
                  });
                  newUnsubs.push(unsubAlt);
                } catch {}
              });
              newUnsubs.push(unsub);
            } catch {}
          });
          liveUnsubs.current = newUnsubs;
        } finally {
          setLoading(false);
        }
      })();
    }, [schoolID, selectedPeriod, periodRange.start, periodRange.end]);
    useEffect(() => {
      return () => {
        liveUnsubs.current.forEach(fn => fn());
        liveUnsubs.current = [];
      };
    }, []);

    const reportTypes = [
      { title: "Income Statement", description: "Revenue vs expenses for selected period", icon: TrendingUp, status: loading ? "processing" : "ready", lastGenerated: new Date().toISOString().slice(0,10) },
      { title: "Balance Sheet", description: "Assets, liabilities, and equity overview", icon: Receipt, status: "processing", lastGenerated: new Date().toISOString().slice(0,10) },
      { title: "Cash Flow Statement", description: "Cash inflows and outflows analysis", icon: DollarSign, status: loading ? "processing" : "ready", lastGenerated: new Date().toISOString().slice(0,10) },
      { title: "Fee Collection Report", description: "Student fee payments and outstanding balances", icon: Users, status: loading ? "processing" : "ready", lastGenerated: new Date().toISOString().slice(0,10) },
      { title: "Expense Analysis", description: "Detailed breakdown of all expenses by category", icon: Receipt, status: "processing", lastGenerated: new Date().toISOString().slice(0,10) },
      { title: "Payroll Summary", description: "Staff salary costs and statutory deductions", icon: Users, status: "processing", lastGenerated: new Date().toISOString().slice(0,10) },
    ];

    const totalOutstandingAmount = useMemo(() => classSummary.reduce((s, r) => s + r.amountOutstanding, 0), [classSummary]);

    const chartConfig = { income: { label: "Income", color: "#10B981" }, expenses: { label: "Expenses", color: "#EF4444" }, cashFlow: { label: "Cash Flow", color: "#3B82F6" } } as const;

    const getStatusBadge = (status: string) => {
      switch (status) {
        case 'ready':
          return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">Ready</Badge>;
        case 'processing':
          return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">Processing</Badge>;
        default:
          return <Badge variant="secondary">Unknown</Badge>;
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div>
            <h2 className="text-lg font-semibold">Financial Reports & Analytics</h2>
            <p className="text-sm text-muted-foreground">Generate and analyze financial reports</p>
          </div>
          <div className="flex gap-2">
            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Period" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="term">This Term</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => { setPreviewType('income-statement'); setPreviewOpen(true); }} disabled={loading}>
              <Download className="h-4 w-4 mr-2" /> Export Summary
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-green-600" /> Income vs Expenses</CardTitle>
              <CardDescription>Monthly financial performance {periodLabel ? `(${periodLabel})` : ''}</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={incomeVsExpenses}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" fontSize={12} />
                    <YAxis fontSize={12} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="income" fill="#10B981" />
                    <Bar dataKey="expenses" fill="#EF4444" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><DollarSign className="h-5 w-5 text-blue-600" /> Cash Flow Trend</CardTitle>
              <CardDescription>Monthly cash flow analysis {periodLabel ? `(${periodLabel})` : ''}</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={cashFlowData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" fontSize={12} />
                    <YAxis fontSize={12} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line type="monotone" dataKey="cashFlow" stroke="#3B82F6" strokeWidth={3} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Available Reports</CardTitle>
            <CardDescription>Generate and download financial reports</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reportTypes.map((report, index) => (
                <div key={index} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                        <report.icon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm">{report.title}</h4>
                        {getStatusBadge(report.status)}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">{report.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Last: {report.lastGenerated}</span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={report.status === 'processing'} onClick={() => {
                        if (report.title === 'Fee Collection Report') { setPreviewType('fee-collection'); setPreviewOpen(true); }
                        if (report.title === 'Income Statement') { setPreviewType('income-statement'); setPreviewOpen(true); }
                      }}>
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button size="sm" disabled={report.status === 'processing'} onClick={() => {
                        if (report.title === 'Fee Collection Report') { setPreviewType('fee-collection'); setPreviewOpen(true); setTimeout(() => printElement(printRef.current), 200); }
                        if (report.title === 'Income Statement') { setPreviewType('income-statement'); setPreviewOpen(true); setTimeout(() => printElement(printRef.current), 200); }
                      }}>
                        <Download className="h-3 w-3 mr-1" /> PDF
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-orange-600" /> Outstanding Fees by Class</CardTitle>
            <CardDescription>Fee collection status across all grades</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3">Class</th>
                    <th className="text-left p-3">Total Students</th>
                    <th className="text-left p-3">Paid</th>
                    <th className="text-left p-3">Outstanding</th>
                    <th className="text-left p-3">Amount Due</th>
                    <th className="text-left p-3">Collection Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {classSummary.map((item, index) => {
                    const collectionRate = item.students ? ((item.collected / item.students) * 100).toFixed(1) : '0.0';
                    return (
                      <tr key={index} className="border-b hover:bg-muted/30">
                        <td className="p-3 font-medium">{item.className}</td>
                        <td className="p-3">{item.students}</td>
                        <td className="p-3 text-green-600">{item.collected}</td>
                        <td className="p-3 text-red-600">{item.outstanding}</td>
                        <td className="p-3 font-bold">KES {item.amountOutstanding.toLocaleString()}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${collectionRate}%` }} />
                            </div>
                            <span className="text-sm">{collectionRate}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!loading && classSummary.length === 0 && (
                <div className="text-center py-6 text-sm text-muted-foreground">No data available. Ensure students and transactions exist for this school.</div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Report Actions</CardTitle>
            <CardDescription>Generate commonly requested reports</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button variant="outline" className="h-auto p-4 flex flex-col gap-2" onClick={() => { setPreviewType('income-statement'); setPreviewOpen(true); }} disabled={loading}>
                <FileText className="h-6 w-6" />
                <span className="text-sm">Monthly Summary</span>
              </Button>
              <Button variant="outline" className="h-auto p-4 flex flex-col gap-2" onClick={() => { setPreviewType('fee-collection'); setPreviewOpen(true); }} disabled={loading}>
                <DollarSign className="h-6 w-6" />
                <span className="text-sm">Fee Defaulters</span>
              </Button>
              <Button variant="outline" className="h-auto p-4 flex flex-col gap-2" disabled>
                <Receipt className="h-6 w-6" />
                <span className="text-sm">Expense Report</span>
              </Button>
              <Button variant="outline" className="h-auto p-4 flex flex-col gap-2" disabled>
                <Users className="h-6 w-6" />
                <span className="text-sm">Payroll Report</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle>Report Preview</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <style>{reportStyles}</style>
              <div className="flex justify-end gap-2 print:hidden">
                <Button variant="outline" size="sm" onClick={() => printElement(printRef.current)} disabled={loading}>
                  <Download className="h-4 w-4 mr-2" /> Download PDF
                </Button>
              </div>
              <div ref={printRef} className="report-container">
                <ReportHeader schoolName={schoolName} schoolLogo={schoolLogo} schoolAddress={schoolAddress} />
                {previewType === 'fee-collection' && (
                  <FeeCollectionReport classSummary={classSummary} totalOutstandingAmount={totalOutstandingAmount} />
                )}
                {previewType === 'income-statement' && (
                  <IncomeStatementReport incomeVsExpenses={incomeVsExpenses} />
                )}
                <ReportFooter />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  export default FinanceReports;

  function printElement(node: HTMLElement | null) {
    if (!node) { window.print(); return; }
    const html = node.outerHTML;
    const w = window.open('', '_blank', 'width=1024,height=768');
    if (!w) { window.print(); return; }
    w.document.open();
    w.document.write(`<!doctype html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>Financial Report</title>
        <style>
          ${reportStyles}
        </style>
      </head>
      <body>${html}</body>
    </html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); w.close(); }, 500);
  }

  function ReportHeader({ schoolName, schoolLogo, schoolAddress }: { schoolName: string; schoolLogo: string | null; schoolAddress: string }) {
    return (
      <div className="report-header">
        <div className="flex items-start gap-4">
          {schoolLogo && <img src={schoolLogo} alt="Logo" className="h-16 w-16 object-contain" />}
          <div className="school-info">
            <h1>{schoolName}</h1>
            <p>{schoolAddress}</p>
            <p className="mt-1">Official Financial Report</p>
          </div>
        </div>
        <div className="report-meta">
          <h2>Report Details</h2>
          <div className="meta-item">Generated: {new Date().toLocaleDateString()}</div>
          <div className="meta-item">Time: {new Date().toLocaleTimeString()}</div>
          <div className="meta-item">Ref: {Math.random().toString(36).substr(2, 9).toUpperCase()}</div>
        </div>
      </div>
    );
  }

  function ReportFooter() {
    return (
      <div className="report-footer">
        <div>Generated by VOCHAK School Management System</div>
        <div>Page 1 of 1</div>
        <div>Confidential Document</div>
      </div>
    );
  }

  function FeeCollectionReport({ classSummary, totalOutstandingAmount }: { classSummary: ClassSummaryRow[]; totalOutstandingAmount: number; }) {
    const totalCollected = classSummary.reduce((acc, curr) => acc + curr.amountCollected, 0);
    const totalExpected = totalCollected + totalOutstandingAmount;
    const overallRate = totalExpected > 0 ? ((totalCollected / totalExpected) * 100).toFixed(1) : '0.0';

    return (
      <div>
        <div className="section-title">Fee Collection Overview</div>
        
        <div className="summary-grid">
          <div className="summary-card">
            <div className="summary-label">Total Expected</div>
            <div className="summary-value">KES {totalExpected.toLocaleString()}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Total Collected</div>
            <div className="summary-value text-green">KES {totalCollected.toLocaleString()}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Outstanding Balance</div>
            <div className="summary-value text-red">KES {totalOutstandingAmount.toLocaleString()}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Collection Rate</div>
            <div className="summary-value">{overallRate}%</div>
          </div>
        </div>

        <div className="section-title">Class Breakdown</div>
        <table>
          <thead>
            <tr>
              <th>Class</th>
              <th className="text-right">Students</th>
              <th className="text-right">Paid Full</th>
              <th className="text-right">With Balance</th>
              <th className="text-right">Collected (KES)</th>
              <th className="text-right">Outstanding (KES)</th>
              <th className="text-right">Rate</th>
            </tr>
          </thead>
          <tbody>
            {classSummary.map((r) => {
              const rate = r.students ? Math.round((r.collected / r.students) * 100) : 0;
              return (
                <tr key={r.className} className="no-break">
                  <td className="font-medium">{r.className}</td>
                  <td className="text-right">{r.students}</td>
                  <td className="text-right text-green">{r.collected}</td>
                  <td className="text-right text-red">{r.outstanding}</td>
                  <td className="text-right">{r.amountCollected.toLocaleString()}</td>
                  <td className="text-right font-bold">{r.amountOutstanding.toLocaleString()}</td>
                  <td className="text-right">{rate}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {classSummary.length === 0 && (
          <p className="text-center text-gray-500 py-8">No data available for this report.</p>
        )}
      </div>
    );
  }

  function IncomeStatementReport({ incomeVsExpenses }: { incomeVsExpenses: Array<{ month: string; income: number; expenses: number }>; }) {
    const totals = incomeVsExpenses.reduce((a, x) => { a.income += x.income; a.expenses += x.expenses; return a; }, { income: 0, expenses: 0 });
    const net = totals.income - totals.expenses;
    
    return (
      <div>
        <div className="section-title">Income Statement Summary</div>
        
        <div className="summary-grid">
          <div className="summary-card">
            <div className="summary-label">Total Income</div>
            <div className="summary-value text-green">KES {totals.income.toLocaleString()}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Total Expenses</div>
            <div className="summary-value text-red">KES {totals.expenses.toLocaleString()}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Net Cash Flow</div>
            <div className={`summary-value ${net >= 0 ? 'text-green' : 'text-red'}`}>
              KES {net.toLocaleString()}
            </div>
          </div>
        </div>

        <div className="section-title">Monthly Breakdown</div>
        <table>
          <thead>
            <tr>
              <th>Month</th>
              <th className="text-right">Income</th>
              <th className="text-right">Expenses</th>
              <th className="text-right">Net Flow</th>
              <th className="text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {incomeVsExpenses.map((r) => {
              const monthNet = r.income - r.expenses;
              return (
                <tr key={r.month} className="no-break">
                  <td className="font-medium">{r.month}</td>
                  <td className="text-right text-green">KES {r.income.toLocaleString()}</td>
                  <td className="text-right text-red">KES {r.expenses.toLocaleString()}</td>
                  <td className="text-right font-bold">KES {monthNet.toLocaleString()}</td>
                  <td className="text-right">
                    <span style={{ 
                      padding: '2px 6px', 
                      borderRadius: '4px', 
                      fontSize: '10px',
                      background: monthNet >= 0 ? '#dcfce7' : '#fee2e2',
                      color: monthNet >= 0 ? '#166534' : '#991b1b'
                    }}>
                      {monthNet >= 0 ? 'SURPLUS' : 'DEFICIT'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {incomeVsExpenses.length === 0 && (
          <p className="text-center text-gray-500 py-8">No transactions found to build this report.</p>
        )}
      </div>
    );
  }